/**
 * useVoiceInput — React hook for push-to-talk Speech-to-Text.
 *
 * Primary: Web Speech API (SpeechRecognition / webkitSpeechRecognition)
 *   — works natively in Chrome, Edge, Safari with interim results.
 * Fallback: MediaRecorder → POST to /api/v1/expert/stt (Whisper via OpenAI)
 *   — used automatically in Firefox or when Web Speech API is unavailable.
 *
 * Push-to-talk model: call startRecording() to begin, stopRecording() to end.
 * Auto-stops after 60 seconds with a warning.
 */

import { useState, useRef, useCallback, useEffect } from 'react';

export type PermissionState = 'unknown' | 'granted' | 'denied' | 'prompt';

export interface UseVoiceInputReturn {
  /** Begin recording / listening. */
  startRecording: () => Promise<void>;
  /** Stop recording / listening and finalise transcript. */
  stopRecording: () => void;
  /** Whether the mic is actively capturing audio. */
  isRecording: boolean;
  /** Whether the Whisper fallback is running (only true during upload+transcription). */
  isTranscribing: boolean;
  /** Current microphone permission state. */
  permissionState: PermissionState;
  /** Live transcript text (interim for Web Speech, final for Whisper). */
  transcript: string;
  /** Clear the current transcript. */
  clearTranscript: () => void;
  /** Whether Web Speech API is available in this browser. */
  isWebSpeechSupported: boolean;
}

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8001';
const MAX_RECORDING_MS = 60_000; // 60 seconds hard limit
// ~1 MB limit — 60 s of webm/opus is typically 100–300 KB, 2 MB is generous
const MAX_AUDIO_BYTES = 2 * 1024 * 1024;

/** Retrieve stored JWT for API calls. Must match the key used by expertApi.ts. */
function getToken(): string | null {
  try {
    return localStorage.getItem('token');
  } catch {
    return null;
  }
}

/** True if the browser exposes SpeechRecognition (or webkit prefix). */
function detectWebSpeech(): boolean {
  return (
    typeof window !== 'undefined' &&
    ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window)
  );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type SpeechRecognitionCtor = new () => SpeechRecognitionInstance;

interface SpeechRecognitionInstance extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  onerror: ((event: SpeechRecognitionErrorEvent) => void) | null;
  onend: (() => void) | null;
}

interface SpeechRecognitionEvent {
  resultIndex: number;
  results: SpeechRecognitionResultList;
}

interface SpeechRecognitionResultList {
  length: number;
  item(index: number): SpeechRecognitionResult;
  [index: number]: SpeechRecognitionResult;
}

interface SpeechRecognitionResult {
  isFinal: boolean;
  [index: number]: SpeechRecognitionAlternative;
}

interface SpeechRecognitionAlternative {
  transcript: string;
}

interface SpeechRecognitionErrorEvent {
  error: string;
  message?: string;
}

function getSpeechRecognitionCtor(): SpeechRecognitionCtor | null {
  if (typeof window === 'undefined') return null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const w = window as any;
  return w.SpeechRecognition || w.webkitSpeechRecognition || null;
}

export function useVoiceInput(): UseVoiceInputReturn {
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [permissionState, setPermissionState] = useState<PermissionState>('unknown');
  const [transcript, setTranscript] = useState('');
  const isWebSpeechSupported = detectWebSpeech();

  // Web Speech API refs
  const recognitionRef = useRef<SpeechRecognitionInstance | null>(null);
  const finalTranscriptRef = useRef('');

  // MediaRecorder fallback refs
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);

  // Auto-stop timer
  const autoStopTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ─── Cleanup ──────────────────────────────────────────────────────────────

  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  const clearAutoStop = useCallback(() => {
    if (autoStopTimerRef.current !== null) {
      clearTimeout(autoStopTimerRef.current);
      autoStopTimerRef.current = null;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearAutoStop();
      stopStream();
      if (recognitionRef.current) {
        try { recognitionRef.current.abort(); } catch { /* ignore */ }
        recognitionRef.current = null;
      }
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        try { mediaRecorderRef.current.stop(); } catch { /* ignore */ }
      }
    };
  }, [clearAutoStop, stopStream]);

  // ─── Check permission state ────────────────────────────────────────────────

  useEffect(() => {
    if (typeof navigator === 'undefined' || !navigator.permissions) return;
    navigator.permissions
      .query({ name: 'microphone' as PermissionName })
      .then((status) => {
        setPermissionState(status.state as PermissionState);
        status.onchange = () => setPermissionState(status.state as PermissionState);
      })
      .catch(() => setPermissionState('unknown'));
  }, []);

  // ─── Whisper fallback transcription ───────────────────────────────────────

  const transcribeWithWhisper = useCallback(async (blob: Blob) => {
    if (blob.size === 0) return;
    if (blob.size > MAX_AUDIO_BYTES) {
      console.warn('useVoiceInput: audio blob too large, skipping Whisper transcription');
      return;
    }

    setIsTranscribing(true);
    try {
      const token = getToken();
      const formData = new FormData();
      formData.append('audio', blob, 'recording.webm');

      const headers: Record<string, string> = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const res = await fetch(`${API_URL}/api/v1/expert/stt`, {
        method: 'POST',
        headers,
        body: formData,
      });

      if (res.ok) {
        const data = await res.json() as { text: string; language?: string };
        if (data.text) {
          setTranscript(data.text.trim());
        }
      } else {
        console.warn('useVoiceInput: Whisper STT request failed', res.status);
      }
    } catch (err) {
      console.error('useVoiceInput: Whisper transcription error', err);
    } finally {
      setIsTranscribing(false);
    }
  }, []);

  // ─── Web Speech API path ───────────────────────────────────────────────────

  const startWebSpeech = useCallback((): boolean => {
    const Ctor = getSpeechRecognitionCtor();
    if (!Ctor) return false;

    // Abort any existing session first
    if (recognitionRef.current) {
      try { recognitionRef.current.abort(); } catch { /* ignore */ }
      recognitionRef.current = null;
    }

    const recognition = new Ctor();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = navigator.language || 'en-US';

    finalTranscriptRef.current = '';
    setTranscript('');

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      let interim = '';
      let final = finalTranscriptRef.current;

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        const text = result[0].transcript;
        if (result.isFinal) {
          final += (final ? ' ' : '') + text.trim();
        } else {
          interim += text;
        }
      }

      finalTranscriptRef.current = final;
      setTranscript(final + (interim ? (final ? ' ' : '') + interim : ''));
    };

    recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
        setPermissionState('denied');
      }
      console.warn('useVoiceInput: SpeechRecognition error', event.error);
      setIsRecording(false);
      clearAutoStop();
    };

    recognition.onend = () => {
      // Use the accumulated final transcript on end
      setTranscript(finalTranscriptRef.current);
      setIsRecording(false);
      clearAutoStop();
    };

    recognitionRef.current = recognition;

    try {
      recognition.start();
      setIsRecording(true);
      return true;
    } catch (err) {
      console.error('useVoiceInput: failed to start SpeechRecognition', err);
      recognitionRef.current = null;
      return false;
    }
  }, [clearAutoStop]);

  const stopWebSpeech = useCallback(() => {
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch { /* ignore */ }
    }
    // onend handler will set isRecording=false
  }, []);

  // ─── MediaRecorder fallback path ───────────────────────────────────────────

  const startMediaRecorder = useCallback(async (): Promise<boolean> => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      setPermissionState('granted');

      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm')
        ? 'audio/webm'
        : '';

      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      chunksRef.current = [];

      recorder.ondataavailable = (e: BlobEvent) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = async () => {
        stopStream();
        setIsRecording(false);
        clearAutoStop();
        const blob = new Blob(chunksRef.current, { type: mimeType || 'audio/webm' });
        chunksRef.current = [];
        await transcribeWithWhisper(blob);
      };

      recorder.start(250); // collect chunks every 250 ms
      mediaRecorderRef.current = recorder;
      setIsRecording(true);
      return true;
    } catch (err) {
      const domErr = err as DOMException;
      if (domErr?.name === 'NotAllowedError' || domErr?.name === 'PermissionDeniedError') {
        setPermissionState('denied');
      }
      console.error('useVoiceInput: getUserMedia error', err);
      return false;
    }
  }, [stopStream, clearAutoStop, transcribeWithWhisper]);

  const stopMediaRecorder = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
  }, []);

  // ─── Public API ───────────────────────────────────────────────────────────

  const startRecording = useCallback(async () => {
    if (isRecording || isTranscribing) return;

    setTranscript('');

    let started: boolean;
    if (isWebSpeechSupported) {
      started = startWebSpeech();
    } else {
      started = await startMediaRecorder();
    }

    if (started) {
      // Auto-stop after MAX_RECORDING_MS
      autoStopTimerRef.current = setTimeout(() => {
        console.warn('useVoiceInput: auto-stop after 60 seconds');
        if (isWebSpeechSupported) {
          stopWebSpeech();
        } else {
          stopMediaRecorder();
        }
      }, MAX_RECORDING_MS);
    }
  }, [
    isRecording,
    isTranscribing,
    isWebSpeechSupported,
    startWebSpeech,
    startMediaRecorder,
    stopWebSpeech,
    stopMediaRecorder,
  ]);

  const stopRecording = useCallback(() => {
    clearAutoStop();
    if (!isRecording) return;

    if (isWebSpeechSupported) {
      stopWebSpeech();
    } else {
      stopMediaRecorder();
    }
  }, [isRecording, isWebSpeechSupported, stopWebSpeech, stopMediaRecorder, clearAutoStop]);

  const clearTranscript = useCallback(() => {
    setTranscript('');
    finalTranscriptRef.current = '';
  }, []);

  return {
    startRecording,
    stopRecording,
    isRecording,
    isTranscribing,
    permissionState,
    transcript,
    clearTranscript,
    isWebSpeechSupported,
  };
}
